import { useCallback, useEffect, useRef, useState } from 'react';
import type { Call, CallAgent, TeamsCaptions, TeamsCaptionsInfo } from '@azure/communication-calling';
import liveAPI from '../../../services/liveApi';
import { TeamsCaptureStatus, TeamsFinalCaption } from '../../../types/live';

// Joins the meeting's Microsoft Teams call as an ACS external participant
// (muted, captions-only) and forwards each FINAL Teams caption — speaker
// display name + text — to onFinalCaption. Mirrors useMicCapture's shape:
// callers allocate sequence numbers and post segments (source "feed").
// teams-live-transcription-plan.md Unit T3.
//
// The ACS Calling SDK is loaded lazily (dynamic import) so the ~1.5 MB bundle
// is only fetched when a controller actually joins a Teams meeting, and so
// jest can mock the module without loading WebRTC internals.

export interface UseTeamsCaptionsResult {
  status: TeamsCaptureStatus;
  error: string | null;
  active: boolean; // convenience: connecting | inLobby | connected | captioning
  join: (joinUrl: string) => void;
  leave: () => void;
}

interface CallResources {
  callAgent: CallAgent;
  call: Call;
  captions: TeamsCaptions | null;
  captionsHandler: ((info: TeamsCaptionsInfo) => void) | null;
}

const ACTIVE_STATUSES: TeamsCaptureStatus[] = ['connecting', 'inLobby', 'connected', 'captioning'];

export const useTeamsCaptions = (
  meetingId: string | undefined,
  onFinalCaption: (caption: TeamsFinalCaption) => void
): UseTeamsCaptionsResult => {
  const [status, setStatus] = useState<TeamsCaptureStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const callbackRef = useRef(onFinalCaption);
  const resourcesRef = useRef<CallResources | null>(null);
  // Drop exact consecutive duplicates: Teams re-finalizes a caption when a
  // speaker turn is amended, and reconnects can replay the last final.
  const lastFinalRef = useRef<string | null>(null);
  const joiningRef = useRef(false);

  useEffect(() => {
    callbackRef.current = onFinalCaption;
  }, [onFinalCaption]);

  const teardown = useCallback(async () => {
    const resources = resourcesRef.current;
    resourcesRef.current = null;
    lastFinalRef.current = null;
    if (!resources) return;
    try {
      if (resources.captions && resources.captionsHandler) {
        resources.captions.off('CaptionsReceived', resources.captionsHandler);
      }
    } catch {
      // ignore
    }
    try {
      if (resources.call.state !== 'Disconnected') {
        await resources.call.hangUp();
      }
    } catch {
      // ignore
    }
    try {
      await resources.callAgent.dispose();
    } catch {
      // ignore
    }
  }, []);

  const join = useCallback(
    (joinUrl: string) => {
      if (!meetingId || joiningRef.current || resourcesRef.current) return;
      joiningRef.current = true;
      setError(null);
      setStatus('connecting');

      (async () => {
        // Token first: a 409 (not controller) or 503 (ACS unconfigured) fails
        // fast before any SDK bytes are downloaded.
        const tokenResponse = await liveAPI.getTeamsToken(meetingId);

        const [{ CallClient, Features }, { AzureCommunicationTokenCredential }] = await Promise.all([
          import('@azure/communication-calling'),
          import('@azure/communication-common'),
        ]);

        const credential = new AzureCommunicationTokenCredential({
          token: tokenResponse.token,
          tokenRefresher: async () => (await liveAPI.getTeamsToken(meetingId)).token,
          refreshProactively: true,
        });

        const callClient = new CallClient();
        const callAgent = await callClient.createCallAgent(credential, {
          displayName: tokenResponse.display_name,
        });
        const call = callAgent.join({ meetingLink: joinUrl }, { audioOptions: { muted: true } });
        resourcesRef.current = { callAgent, call, captions: null, captionsHandler: null };

        // The observer is captions-only. Muting outgoing audio (above) stops it
        // sending audio, but the SDK still renders the call's incoming audio to
        // the controller's speakers by default. On the controller's machine that
        // plays the live meeting back into the room, is picked up by the real
        // mic, and loops into Teams — the "terrible echo repeating my words".
        // Muting incoming audio silences the speaker while captions keep flowing.
        const muteIncoming = async () => {
          try {
            if (!call.isIncomingAudioMuted) {
              await call.muteIncomingAudio();
            }
          } catch {
            // Best-effort: never let this block captions.
          }
        };

        const startCaptions = async () => {
          const resources = resourcesRef.current;
          if (!resources) return;
          const captionsFeature = call.feature(Features.Captions);
          if (captionsFeature.captions.kind !== 'TeamsCaptions') {
            setError('This call does not provide Teams captions');
            setStatus('error');
            return;
          }
          const captions = captionsFeature.captions as TeamsCaptions;
          const handler = (info: TeamsCaptionsInfo) => {
            if (String(info.resultType).toLowerCase() !== 'final') return;
            const text = (info.captionText || info.spokenText || '').trim();
            if (!text) return;
            const speaker = info.speaker?.displayName?.trim() || 'Teams participant';
            const key = `${speaker}|${text}`;
            if (key === lastFinalRef.current) return;
            lastFinalRef.current = key;
            const timestamp =
              info.timestamp instanceof Date ? info.timestamp.toISOString() : new Date().toISOString();
            callbackRef.current({ speaker, text, timestamp });
          };
          captions.on('CaptionsReceived', handler);
          resources.captions = captions;
          resources.captionsHandler = handler;
          try {
            if (!captions.isCaptionsFeatureActive) {
              await captions.startCaptions({ spokenLanguage: 'en-us' });
            }
            setStatus('captioning');
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to start Teams captions');
            setStatus('error');
          }
        };

        call.on('stateChanged', () => {
          switch (call.state) {
            case 'InLobby':
              setStatus('inLobby');
              break;
            case 'Connected':
              setStatus('connected');
              void muteIncoming();
              void startCaptions();
              break;
            case 'Disconnected': {
              const reason = call.callEndReason;
              // 0 = clean hang-up; anything else surfaces as a warning.
              if (reason && reason.code !== 0) {
                setError(`Teams call ended (code ${reason.code}/${reason.subCode ?? 0})`);
                setStatus('error');
              } else {
                setStatus('idle');
              }
              void teardown();
              break;
            }
            default:
              break;
          }
        });
      })()
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to join Teams meeting');
          setStatus('error');
          void teardown();
        })
        .finally(() => {
          joiningRef.current = false;
        });
    },
    [meetingId, teardown]
  );

  const leave = useCallback(() => {
    setStatus('idle');
    setError(null);
    void teardown();
  }, [teardown]);

  // Hang up if the page unmounts mid-session.
  useEffect(() => {
    return () => {
      void teardown();
    };
  }, [teardown]);

  return { status, error, active: ACTIVE_STATUSES.includes(status), join, leave };
};
